import { E as EVENTS } from "./events-58bc9098.js";

const { MODAL_POPUP_OPEN, MODAL_POPUP_CLOSE } = EVENTS;

const MODAL_ID = "wholesaleCheckoutConfirm";
const CHECKOUT_BUTTON_SELECTOR = ".cart-checkout-button";
const CONFIRMED_FLAG = "wholesaleCheckoutConfirmed";
const CONSENT_ATTRIBUTE_NAME = "attributes[Order review confirmed]";

/**
 * @class WholesaleCheckoutConfirmModal
 * @description Intercepts a click on any ".cart-checkout-button" element (event delegation,
 * capture phase, scoped by class since duplicate "checkout" ids can exist in the DOM at once
 * across the cart drawer and the cart page) and shows a review-your-cart modal before letting
 * the click through to Shopify's native "checkout" form submission.
 *
 * The confirmed state is tracked as a data attribute on the exact button node that was clicked,
 * not globally, so the other (unconfirmed) duplicate-id button still shows the modal on its own
 * first click.
 */
class WholesaleCheckoutConfirmModal extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      dialog: "dialog",
      primaryButton: "[js-wholesale-checkout-confirm-primary]",
      secondaryButton: "[js-wholesale-checkout-confirm-secondary]",
      checkboxes: ".wholesale-checkout-confirm__checkbox-input",
    };

    this.pendingButton = null;
  }

  connectedCallback() {
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.checkboxRequired = this.dataset.checkboxRequired === "true";
    this.dialog = this.querySelector(this.selectors.dialog);
    this.primaryButton = this.querySelector(this.selectors.primaryButton);
    this.secondaryButton = this.querySelector(this.selectors.secondaryButton);
    // Every acknowledgement checkbox in the modal (the testers checkbox and the review
    // checkbox) must be checked before the primary button enables.
    this.checkboxes = this.checkboxRequired
      ? [...this.querySelectorAll(this.selectors.checkboxes)]
      : [];

    // Capture phase so this runs before any other click handler bound to (or bubbling through)
    // the checkout button, and before the browser's native form-submission default action fires.
    document.addEventListener(
      "click",
      this.#handleDocumentClick.bind(this),
      { capture: true, signal },
    );

    this.primaryButton?.addEventListener(
      "click",
      this.#handleConfirm.bind(this),
      { signal },
    );

    this.secondaryButton?.addEventListener(
      "click",
      this.#handleCancel.bind(this),
      { signal },
    );

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener(
        "change",
        this.#handleCheckboxChange.bind(this),
        { signal },
      );
    });

    // The theme's Dialog class already binds its own close-button handler on the dialog
    // element, but the X was reported as doing nothing on this modal. Route it through the
    // same close path the secondary button uses, which is known to work here, and reset the
    // pending button so a later checkout click still opens the modal. If the theme's own
    // handler also fires, the extra close() is a no-op on an already-closing dialog.
    this.dialog?.addEventListener(
      "click",
      this.#handleCloseButtonClick.bind(this),
      { signal },
    );

    // Esc key. The theme preventDefaults "cancel" to animate out; mirror the state reset.
    this.dialog?.addEventListener(
      "cancel",
      () => {
        this.pendingButton = null;
      },
      { signal },
    );
  }

  disconnectedCallback() {
    this.controller.abort();
  }

  #handleDocumentClick(event) {
    const button = event.target.closest(CHECKOUT_BUTTON_SELECTOR);

    if (!button) return;

    // Already confirmed on this exact node: let the native "checkout" form submission through
    // untouched. Do not preventDefault, do not reopen the modal.
    if (button.dataset[CONFIRMED_FLAG] === "true") return;

    event.preventDefault();

    this.pendingButton = button;

    if (this.checkboxes.length) {
      this.checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
      this.#updatePrimaryButtonState();
    }

    if (!this.dialog?.open) {
      window.dispatchEvent(new CustomEvent(MODAL_POPUP_OPEN(MODAL_ID)));
    }
  }

  #handleConfirm(event) {
    event.preventDefault();

    // Defensive: the primary button is already disabled via the "disabled" attribute while the
    // checkbox variant is unacknowledged, so this should be unreachable, but guard anyway.
    if (this.checkboxRequired && !this.#allCheckboxesChecked()) return;

    window.dispatchEvent(new CustomEvent(MODAL_POPUP_CLOSE(MODAL_ID)));

    const button = this.pendingButton;
    this.pendingButton = null;

    if (!button) return;

    button.dataset[CONFIRMED_FLAG] = "true";

    this.#stampConsent(button);

    // Re-dispatch a real click on the exact node the buyer originally clicked. The delegated
    // listener above sees the confirmed flag on this node and lets this click through
    // untouched, so the native "checkout" form submission proceeds normally.
    button.click();
  }

  #handleCancel(event) {
    event.preventDefault();

    window.dispatchEvent(new CustomEvent(MODAL_POPUP_CLOSE(MODAL_ID)));

    this.pendingButton = null;
  }

  #handleCloseButtonClick(event) {
    if (!event.target.closest("form[method='dialog']")) return;

    // Stop the native dialog-form submission so the theme can animate the close.
    event.preventDefault();

    window.dispatchEvent(new CustomEvent(MODAL_POPUP_CLOSE(MODAL_ID)));

    this.pendingButton = null;
  }

  #handleCheckboxChange() {
    this.#updatePrimaryButtonState();
  }

  /**
   * Records the buyer's confirmation on the order itself as a cart attribute, so consent is
   * auditable per-order in Shopify admin ("Additional details" on the order page) and on the
   * Order API. The attribute rides the native cart-form submission as a hidden input rather
   * than an async /cart/update.js call, which could race the redirect to checkout.
   *
   * Stamped on every confirm, not only when the checkbox variant is enabled, so toggling the
   * checkbox setting off in the theme editor does not silently drop the audit trail.
   */
  #stampConsent(button) {
    // The checkout button is attached to its cart form via the "form" attribute, so
    // button.form resolves the exact form being submitted (cart drawer or cart page).
    const form = button.form;

    if (!form) return;

    let input = form.querySelector(`input[name="${CONSENT_ATTRIBUTE_NAME}"]`);

    if (!input) {
      input = document.createElement("input");
      input.type = "hidden";
      input.name = CONSENT_ATTRIBUTE_NAME;
      form.appendChild(input);
    }

    // Store-local time (Tatine is Chicago-based) so the stamp reads naturally in admin.
    const stamp = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/Chicago",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date());

    input.value = this.checkboxRequired
      ? `${stamp} CT — checkbox acknowledged`
      : `${stamp} CT`;
  }

  #updatePrimaryButtonState() {
    if (!this.primaryButton) return;

    this.primaryButton.disabled = !this.#allCheckboxesChecked();
  }

  #allCheckboxesChecked() {
    return (
      this.checkboxes.length > 0 &&
      this.checkboxes.every((checkbox) => checkbox.checked)
    );
  }
}

if (!customElements.get("wholesale-checkout-confirm-modal")) {
  customElements.define(
    "wholesale-checkout-confirm-modal",
    WholesaleCheckoutConfirmModal,
  );
}
